<script setup lang="ts">
import { MtdField, MtdModalMobile, MtdTypography } from '@paygine/mtd-uikit'
import { computed, ref } from 'vue'

import { EmptyResultIcon, SearchIcon } from '@/shared/icons'

type ListItem = {
    excludeFromAlphabet?: boolean
    iconUrl?: string
    id: number | string
    label: string
    pinned?: boolean
}

type RenderItem =
    | { item: ListItem; section: 'alphabet' | 'pinned'; type: 'item' }
    | { letter: string; type: 'divider' }

const props = defineProps<{
    alphabetical?: boolean
    empty: {
        subtitle?: string
        title?: string
    }
    items: ListItem[]
    placeholder?: string
    title: string
}>()

const isOpen = defineModel<boolean>()

const emit = defineEmits<{
    select: [ListItem]
}>()

const search = ref('')

const normalizedQuery = computed(() => search.value.trim().toLowerCase())

function buildAlphabetItems(items: ListItem[], alphabetical: boolean): RenderItem[] {
    if (!alphabetical) {
        return items.map((item) => ({
            item,
            section: 'alphabet',
            type: 'item',
        }))
    }

    const sorted = [...items].sort((a, b) => a.label.localeCompare(b.label, 'ru'))

    const result: RenderItem[] = []
    let lastLetter = ''

    for (const item of sorted) {
        const letter = item.label.trim()[0]?.toUpperCase()

        if (letter && letter !== lastLetter) {
            result.push({ letter, type: 'divider' })
            lastLetter = letter
        }

        result.push({ item, section: 'alphabet', type: 'item' } as const)
    }

    return result
}

const preparedItems = computed<RenderItem[]>(() => {
    const query = normalizedQuery.value

    // ПОИСК
    if (query) {
        const lowerQuery = query.toLowerCase()

        const foundItems = props.items.filter((item) =>
            item.label.toLowerCase().includes(lowerQuery)
        )

        const searchOnly = foundItems.filter((item) => item.excludeFromAlphabet === true)

        const alphabetItems = foundItems.filter((item) => item.excludeFromAlphabet !== true)

        return [
            ...searchOnly.map(
                (item) =>
                    ({
                        item,
                        section: 'alphabet',
                        type: 'item',
                    }) as const
            ),
            ...buildAlphabetItems(alphabetItems, props.alphabetical),
        ]
    }

    // БЕЗ ПОИСКА
    const pinned = props.items
        .filter((item) => item.pinned)
        .map(
            (item) =>
                ({
                    item,
                    section: 'pinned',
                    type: 'item',
                }) as const
        )

    const alphabetItems = props.items.filter((item) => !item.excludeFromAlphabet)

    return [
        ...(props.alphabetical ? pinned : pinned.filter((item) => item.item.excludeFromAlphabet)),
        ...buildAlphabetItems(alphabetItems, props.alphabetical),
    ]
})

function selectItem(item: ListItem) {
    isOpen.value = false
    emit('select', item)
}
</script>

<template>
    <MtdModalMobile
        v-model="isOpen"
        container-class="list-container"
        @update:model-value="() => (search = '')"
    >
        <template #content>
            <MtdTypography
                variant="header-1"
                class="default-layout__title"
                tag="h1"
                >{{ props.title }}
            </MtdTypography>

            <MtdField
                v-model="search"
                wrapper-class="search"
                type="text"
                :placeholder="placeholder || 'Поиск'"
            >
                <template #startIcon><SearchIcon /></template>
            </MtdField>

            <div class="list-wrapper">
                <ul class="list">
                    <template
                        v-for="entry in preparedItems"
                        :key="
                            entry.type === 'divider'
                                ? `divider-${entry.letter}`
                                : `${entry.section}-${entry.item.id}`
                        "
                    >
                        <li
                            v-if="entry.type === 'divider'"
                            class="list-divider"
                        >
                            {{ entry.letter }}
                        </li>

                        <li
                            v-else
                            class="list-item"
                            @click="selectItem(entry.item)"
                        >
                            <img
                                v-if="entry.item.iconUrl"
                                class="icon"
                                :src="entry.item.iconUrl"
                                :alt="String(entry.item.id)"
                            />

                            <MtdTypography
                                variant="body-2"
                                tag="span"
                            >
                                {{ entry.item.label }}
                            </MtdTypography>
                        </li>
                    </template>
                </ul>

                <div
                    v-if="preparedItems.length === 0"
                    class="empty"
                >
                    <EmptyResultIcon class="empty__icon" />

                    <MtdTypography
                        tag="h3"
                        class="empty__title"
                        variant="header-2"
                    >
                        {{ props.empty.title || 'Ничего не найдено' }}
                    </MtdTypography>

                    <MtdTypography
                        class="empty__subtitle"
                        tag="p"
                        variant="body-2"
                    >
                        {{ props.empty.subtitle || 'Проверьте название' }}
                    </MtdTypography>
                </div>
            </div>
        </template>
    </MtdModalMobile>
</template>

<style lang="scss" src="./VSearchableList.scss"></style>
